import { useSyncExternalStore } from 'react';

export type Route =
  | { name: 'home' }
  | { name: 'note'; id: string }
  | { name: 'new' }
  | { name: 'history' }
  | { name: 'versions'; id: string }
  | { name: 'schedules'; noteId?: string }
  | { name: 'schedule'; id: string }
  | { name: 'upcoming' }
  | { name: 'preview' }
  | { name: 'settings' }
  | { name: 'templates' };

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
  switch (parts[0]) {
    case undefined:
      return { name: 'home' };
    case 'note':
      return parts[1] ? { name: 'note', id: parts[1] } : { name: 'home' };
    case 'new':
      return { name: 'new' };
    case 'history':
      return { name: 'history' };
    case 'versions':
      return parts[1] ? { name: 'versions', id: parts[1] } : { name: 'history' };
    case 'schedules':
      return { name: 'schedules', noteId: parts[1] };
    case 'schedule':
      return parts[1] ? { name: 'schedule', id: parts[1] } : { name: 'schedules' };
    case 'upcoming':
      return { name: 'upcoming' };
    case 'preview':
      return { name: 'preview' };
    case 'settings':
      return { name: 'settings' };
    case 'templates':
      return { name: 'templates' };
    default:
      return { name: 'home' };
  }
}

export function href(r: Route): string {
  switch (r.name) {
    case 'home':
      return '#/';
    case 'note':
      return `#/note/${encodeURIComponent(r.id)}`;
    case 'versions':
      return `#/versions/${encodeURIComponent(r.id)}`;
    case 'schedules':
      return r.noteId ? `#/schedules/${encodeURIComponent(r.noteId)}` : '#/schedules';
    case 'schedule':
      return `#/schedule/${encodeURIComponent(r.id)}`;
    default:
      return `#/${r.name}`;
  }
}

/** Number of in-app pushes, so "back" never leaves the app after a deep link. */
let depth = 0;

export function navigate(r: Route, replace = false): void {
  const h = href(r);
  if (replace) {
    history.replaceState(null, '', h);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else if (location.hash !== h) {
    depth++;
    location.hash = h;
  }
}

export function goBack(fallback: Route = { name: 'home' }): void {
  if (depth > 0) {
    depth--;
    history.back();
  } else navigate(fallback, true);
}

function subscribe(cb: () => void): () => void {
  window.addEventListener('hashchange', cb);
  return () => window.removeEventListener('hashchange', cb);
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, () => location.hash, () => '');
  return parseRoute(hash);
}
